import clsx from 'clsx';
import { type ForwardedRef, forwardRef } from 'react';
import { type ButtonProps, Link, type LinkProps } from 'react-aria-components';

import { Tooltip } from '../Tooltip';
import type { IconDefinition } from '../util';
import { DivButton } from './DivButton';
import { SidebarItemIcon } from './SidebarItem';

interface SidebarActionHookProps {
    /**
     * The icon to display.
     */
    icon: IconDefinition;
    /**
     * The label to display.
     */
    label: string;
    /**
     * Optional class name to apply to the action.
     */
    className?: string;
}

/**
 * Hook to create the props for the sidebar label action.
 */
function useSidebarAction<T extends SidebarActionHookProps>(props: T) {
    const { icon, label, className, ...rest } = props;
    return {
        actionProps: {
            children: <SidebarItemIcon icon={icon} />,
            className: clsx(
                'opacity-0 focus:opacity-100 group-hover/sidebar-section:opacity-100 aria-expanded:opacity-100',
                'group/sidebar-item flex size-6 items-center justify-center rounded',
                'text-base hover:bg-standout-side-active aria-expanded:bg-standout-side-active aria-[current=page]:bg-standout-side-active data-[pressed=true]:bg-standout-side-active',
                '-outline-offset-1 outline-1 outline-primary data-[focus-visible]:outline',
                className
            ),
            ...rest,
        },
    };
}

export interface SidebarActionButtonProps
    extends Omit<ButtonProps, 'children' | 'className'>,
        SidebarActionHookProps {}

/**
 * Display an action button in the sidebar label.
 */
export const SidebarActionButton = forwardRef(function SidebarActionButton(
    props: SidebarActionButtonProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    const { actionProps } = useSidebarAction(props);
    return (
        <Tooltip label={props.label}>
            <DivButton ref={ref} {...actionProps} aria-label={props.label} />
        </Tooltip>
    );
});

export interface SidebarActionLinkProps
    extends Omit<LinkProps, 'children' | 'className'>,
        SidebarActionHookProps {}

/**
 * Display an action link in the sidebar label.
 */
export const SidebarActionLink = forwardRef(function SidebarActionLink(
    props: SidebarActionLinkProps,
    ref: ForwardedRef<HTMLAnchorElement>
) {
    const { actionProps } = useSidebarAction(props);
    return (
        <Tooltip label={props.label}>
            <Link ref={ref} {...actionProps} />
        </Tooltip>
    );
});
